import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment-timezone";
import { useDispatch, useSelector } from "react-redux";
import jobBookedIcon from "../../images/job-booked-legend.svg";
import jobPreInspectionIcon from "../../images/job-pre-inspection-legend.svg";
import jobUnderInspectionIcon from "../../images/job-under-inspection-legend.svg";
import jobCompletedIcon from "../../images/job-completed-legend.svg";

import {
  fetchAppointmentsByDateRange,
  fetchCalendarSettings,
  fetchSectionTemplates,
  fetchAppointmentDetail,
  setView,
  setSelectedDate,
  setDateRange,
  setSelectedEvent,
  selectFilteredEvents,
  selectCalendarLoading,
  selectCalendarView,
  selectCalendarErrors,
  deleteAppointment,
} from "./slice";
import CalendarToolbar from "./components/CalendarToolbar";
import CustomEvent from "./components/CustomEvent";
import CustomWeekHeader from "./components/CustomWeekHeader";
import CustomDateHeader from "./components/CustomDateHeader";
import AppointmentModal from "./components/AppointmentModal";
import JobPopover from "./components/JobPopover";
import JobSetupModal from "./components/JobSetupModal";
import DeleteConfirmationModal from "./components/DeleteConfirmationModal";
import CustomToast from "../../components/common/CustomToast";
import { getDateRange } from "./utils/dateHelpers";
import { getEventColor } from "./utils/eventHelpers";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./jobCalendar.css";

const localizer = momentLocalizer(moment);

const JobCalendar = () => {
  const dispatch = useDispatch();
  const [date, setDate] = useState(new Date());
  const calendarRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [initialSlot, setInitialSlot] = useState(null);
  
  // JobPopover state
  const [showPopover, setShowPopover] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  // JobSetupModal state
  const [showJobSetupModal, setShowJobSetupModal] = useState(false);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  
  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  
  // Connect to Redux store
  const appointmentsFromStore = useSelector(selectFilteredEvents);
  const loading = useSelector(selectCalendarLoading);
  const errors = useSelector(selectCalendarErrors);
  const view = useSelector(selectCalendarView);
  
  // Convert appointments to events with proper start/end for calendar
  const events = useMemo(() => {
    return appointmentsFromStore.map(appointment => ({
      ...appointment,
      start: new Date(appointment.start_time.substring(0, 19).replace('T', ' ')),
      end: new Date(appointment.end_time.substring(0, 19).replace('T', ' ')),
      allDay: false,
    }));
  }, [appointmentsFromStore]);
  
  // Fetch settings and section templates on mount and manage body scroll
  useEffect(() => {
    dispatch(fetchCalendarSettings());
    dispatch(fetchSectionTemplates());
    
    // Add calendar-active class to body when calendar mounts
    document.body.classList.add('calendar-active');
    
    // Handle window resize
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Remove calendar-active class when calendar unmounts
    return () => {
      document.body.classList.remove('calendar-active');
      window.removeEventListener('resize', handleResize);
    };
  }, [dispatch]);
  
  // Fetch appointments when date or view changes
  useEffect(() => {
    const { start, end } = getDateRange(date, view);
    
    // Update date range in store
    dispatch(setDateRange({ start, end }));
    
    // Fetch appointments for the new range
    dispatch(fetchAppointmentsByDateRange({ start, end }));
  }, [dispatch, date, view]);

  // Dynamic height adjustment for calendar rows based on event count
  useEffect(() => {
    const adjustRowHeights = () => {
      if (!calendarRef.current || view !== 'month') return;

      const calendarElement = calendarRef.current;
      const monthRows = calendarElement.querySelectorAll('.rbc-month-row');
      
      monthRows.forEach((row, rowIndex) => {
        const dateCells = row.querySelectorAll('.rbc-date-cell');
        let maxEventsInRow = 0;
        
        // Count events for each date in this row
        dateCells.forEach(dateCell => {
          const dateStr = dateCell.textContent.trim();
          if (!dateStr) return;
          
          const currentDate = moment(date).startOf('month');
          // Handle previous month dates
          if (rowIndex === 0 && parseInt(dateStr) > 15) {
            currentDate.subtract(1, 'month');
          }
          // Handle next month dates  
          if (rowIndex >= 4 && parseInt(dateStr) < 15) {
            currentDate.add(1, 'month');
          }
          currentDate.date(parseInt(dateStr));
          
          // Count events for this specific date
          const eventsForDate = events.filter(event => {
            const eventStart = moment(event.start);
            return eventStart.isSame(currentDate, 'day');
          });
          
          maxEventsInRow = Math.max(maxEventsInRow, eventsForDate.length);
        });
        
        // Calculate dynamic height: base 120px + 25px per additional event after the first
        const baseHeight = 120;
        const eventHeight = 25;
        const dynamicHeight = Math.max(baseHeight, baseHeight + (maxEventsInRow - 1) * eventHeight);
        
        // Apply dynamic height to row elements
        row.style.minHeight = `${dynamicHeight}px`;
        
        const rowBg = row.querySelector('.rbc-row-bg');
        if (rowBg) {
          rowBg.style.minHeight = `${dynamicHeight}px`;
        }
        
        const rowContent = row.querySelector('.rbc-row-content');
        if (rowContent) {
          rowContent.style.minHeight = `${dynamicHeight}px`;
        }
      });
    };

    // Run adjustment after calendar renders
    const timer = setTimeout(adjustRowHeights, 100);
    return () => clearTimeout(timer);
  }, [events, date, view]);

  // Set default scroll position for week view to 7:30 AM
  useEffect(() => {
    if (view === 'week' && !loading.appointments) {
      const timer = setTimeout(() => {
        // Find time content element and scroll to 7:30 AM
        const timeContent = document.querySelector('.rbc-time-content');
        if (timeContent) {
          // If current scroll position corresponds to 10:30 AM when we set 420px,
          // then we need to calculate the correct position for 7:30 AM
          // 420px = 10.5 hours, so 1 hour = 40px
          // 7.5 hours * 40px = 300px
          timeContent.scrollTop = 300; // 7.5 hours * 40px per hour
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [view, date, loading.appointments]);

  const handleNavigate = useCallback((newDate) => {
    setDate(newDate);
    dispatch(setSelectedDate(newDate.toISOString()));
  }, [dispatch]);

  const handleViewChange = useCallback((newView) => {
    dispatch(setView(newView));
  }, [dispatch]);

  const handleSelectSlot = useCallback(({ start, end }) => {
    setSelectedAppointment(null); // Clear edit mode
    setInitialSlot({ start, end });
    setShowModal(true);
  }, []);

  const handleSelectEvent = useCallback(async (event) => {
    dispatch(setSelectedEvent(event));
    
    // Show popover immediately with basic data
    setSelectedAppointment(event);
    setShowPopover(true);
    
    // Fetch detailed appointment data
    try {
      const detailedAppointment = await dispatch(fetchAppointmentDetail(event.id)).unwrap();
      setSelectedAppointment(detailedAppointment);
    } catch (error) {
      console.error("Failed to fetch appointment details:", error);
      // Keep the basic event data if detailed fetch fails
    }
  }, [dispatch]);

  const handleAddAppointment = useCallback((selectedDate) => {
    setSelectedAppointment(null); // Clear edit mode
    setInitialSlot({ start: selectedDate, end: null });
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setSelectedAppointment(null);
    setInitialSlot(null);
  }, []);

  // JobPopover handlers
  const handleClosePopover = useCallback(() => {
    setShowPopover(false);
    setSelectedAppointment(null);
  }, []);

  const handleDeleteAppointment = useCallback((appointment) => {
    setAppointmentToDelete(appointment);
    setShowDeleteModal(true);
    handleClosePopover();
  }, [handleClosePopover]);

  const handleConfirmDelete = useCallback(async () => {
    if (!appointmentToDelete) return;
    
    try {
      await dispatch(deleteAppointment(appointmentToDelete.id)).unwrap();
      setToastMessage("Job deleted successfully.");
      setToastVariant("success");
      setShowToast(true);
      setShowDeleteModal(false);
      setAppointmentToDelete(null);
    } catch (error) {
      console.error("Failed to delete appointment:", error);
      setToastMessage("Failed to delete job.");
      setToastVariant("error");
      setShowToast(true);
    }
  }, [dispatch, appointmentToDelete]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setAppointmentToDelete(null);
  }, []);

  const handleJobBooking = useCallback((appointment) => {
    // Close popover first
    setShowPopover(false);

    // Use setTimeout to ensure the popover closes before opening AppointmentModal
    setTimeout(() => {
      setSelectedAppointment(appointment);
      setInitialSlot(null);
      setShowModal(true);
    }, 50);
  }, []);

  const handleJobSetup = useCallback((appointment) => {
    // Close popover first
    setShowPopover(false);
    
    // Use setTimeout to ensure the popover closes before opening JobSetupModal
    setTimeout(() => {
      setSelectedAppointment(appointment);
      setShowJobSetupModal(true);
    }, 50);
  }, []);

  const handleCloseJobSetupModal = useCallback(() => {
    setShowJobSetupModal(false);
    setSelectedAppointment(null);
  }, []);

  const eventStyleGetter = useCallback((event) => {
    const tag = event.tag || event.status || "booked";
    const colors = getEventColor(tag);
    
    return {
      style: {
        backgroundColor: colors.background,
        borderLeft: `3px solid ${colors.border}`,
        color: colors.text,
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: "500",
        border: "none",
        cursor: "pointer",
        transition: "all 0.2s ease",
      },
    };
  }, []);

  // Create custom date header component with callback
  const CustomDateHeaderWithCallback = useCallback((props) => {
    return <CustomDateHeader {...props} onAddAppointment={handleAddAppointment} />;
  }, [handleAddAppointment]);

  // Create custom event component with view prop
  const CustomEventWithView = useCallback((props) => {
    return <CustomEvent {...props} view={view} />;
  }, [view]);

  // Generate title based on screen size and current date/view
  const getPageTitle = () => {
    const currentDate = moment(date);
    if (windowWidth <= 768) {
      if (view === "month") {
        return currentDate.format("MMMM YYYY");
      } else if (view === "week") {
        const start = currentDate.clone().startOf("week");
        const end = currentDate.clone().endOf("week");
        return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
      } else if (view === "day") {
        return currentDate.format("dddd, MMMM D, YYYY");
      }
      return currentDate.format("MMMM YYYY");
    }
    return "Job Calendar";
  };

  return (
    <div className="job-calendar-container">
      <div className="calendar-header">
        <h2 className="page-title">{getPageTitle()}</h2>
        <div className="status-legend">
          <div className="legend-item booked">
            <div className="icon-circle" style={{backgroundColor: '#FFC107'}}>
              <img src={jobBookedIcon} alt="Booked" width="16" height="16" />
            </div>
            <span className="legend-text">Booked</span>
          </div>
          <div className="legend-item pre-inspection">
            <div className="icon-circle" style={{backgroundColor: '#0DCAF0'}}>
              <img src={jobPreInspectionIcon} alt="Pre-inspection" width="16" height="16" />
            </div>
            <span className="legend-text">Pre-inspection</span>
          </div>
          <div className="legend-item under-inspection">
            <div className="icon-circle" style={{backgroundColor: '#075AD8'}}>
              <img src={jobUnderInspectionIcon} alt="Under Inspection" width="16" height="16" />
            </div>
            <span className="legend-text">Under Inspection</span>
          </div>
          <div className="legend-item completed">
            <div className="icon-circle" style={{backgroundColor: '#6C757D'}}>
              <img src={jobCompletedIcon} alt="Completed" width="16" height="16" />
            </div>
            <span className="legend-text">Completed</span>
          </div>
        </div>
      </div>

      <div className="calendar-wrapper">
        {errors.appointments && (
          <div className="alert alert-danger d-flex align-items-center mb-3" role="alert">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            <div>
              <strong>Error loading calendar:</strong> {errors.appointments}
              <button
                className="btn btn-sm btn-outline-danger ms-2"
                onClick={() => {
                  const { start, end } = getDateRange(date, view);
                  dispatch(fetchAppointmentsByDateRange({ start, end }));
                }}
              >
                Retry
              </button>
            </div>
          </div>
        )}
        
        {loading.appointments ? (
          <div className="calendar-loading">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading calendar...</span>
            </div>
          </div>
        ) : (
          <div ref={calendarRef}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              date={date}
              view={view}
              onNavigate={handleNavigate}
              onView={handleViewChange}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              selectable
              popup={false}
              showAllEvents={true}
              eventPropGetter={eventStyleGetter}
              style={{ height: "auto", minHeight: "600px" }}
              views={["month", "week", "day", "agenda"]}
              defaultView="month"
              components={{
                toolbar: CalendarToolbar,
                event: CustomEventWithView,
                week: {
                  header: CustomWeekHeader,
                },
                month: {
                  dateHeader: CustomDateHeaderWithCallback,
                },
              }}
            />
          </div>
        )}
      </div>

      <AppointmentModal
        show={showModal}
        onHide={handleCloseModal}
        appointment={selectedAppointment}
        initialSlot={initialSlot}
      />

      {/* JobPopover */}
      <JobPopover
        show={showPopover}
        onHide={handleClosePopover}
        appointment={selectedAppointment}
        onDelete={handleDeleteAppointment}
        onJobBooking={handleJobBooking}
        onJobSetup={handleJobSetup}
        loading={loading.appointmentDetail}
      />

      {/* JobSetupModal */}
      <JobSetupModal
        show={showJobSetupModal}
        onHide={handleCloseJobSetupModal}
        appointment={selectedAppointment}
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        loading={loading.delete}
      />
      
      {/* Toast */}
      <CustomToast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
        position="top-end"
        delay={4000}
        autohide={true}
      />
    </div>
  );
};

export default JobCalendar;